import { EmptyState, PagedFooter } from '../../../components/ui';
import { usePagination } from '../../../lib/usePagination';
import Box from './Box';
import Badge from '../../../components/ui/Badge';
import {
  FaArrowDown, FaArrowUp, FaUserClock, FaCheck,
} from '../../../components/ui/icons';
import type { Anomaly, AnomalyType } from '../../../api/types';
import { possessive } from '../lib/format';

interface Props {
  anomalies: Anomaly[];
  /** The student's gender, for the "vs his own baseline" note. */
  gender?: string | null;
}

const TYPE_META: Record<AnomalyType, {
  label: string;
  icon: React.ReactNode;
}> = {
  drop:    { label: 'Drop',    icon: <FaArrowDown /> },
  silence: { label: 'Silence', icon: <FaUserClock /> },
  spike:   { label: 'Spike',   icon: <FaArrowUp /> },
};

function relativeDate(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const days = Math.round((now - then) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 0) return 'in the future';
    return `${days} days ago`;
  } catch {
    return iso;
  }
}

export default function AnomalyPanel({ anomalies, gender }: Props) {
  const pg = usePagination(anomalies, 5, anomalies.length);
  if (anomalies.length === 0) {
    return (
      <Box title="Anomalies" help="anomalies">
        <EmptyState compact
          icon={<FaCheck />}
          title="No recent anomalies"
          body="Academic behavior looks consistent." />
      </Box>
    );
  }

  return (
    <Box title="Anomalies" help="anomalies" actions={<span className="text-xs text-muted">vs {possessive(gender)} own baseline</span>}>
      <ul>
        {pg.pageItems.map((a) => {
          const meta = TYPE_META[a.anomaly_type];
          const resolved = a.resolved_at !== null;
          return (
            <li key={a.id} className="flex items-start gap-3 py-2.5 text-sm first:pt-0 last:pb-0">
              <span aria-hidden="true" className="grid h-9 w-9 flex-none place-items-center rounded-[11px] bg-accent-bg text-accent-ink">{meta.icon}</span>
              <div className="min-w-0 flex-1">
                <b className="block font-semibold text-ink">{meta.label}</b>
                <div className={`text-[13px] ${resolved ? 'text-muted line-through' : 'text-muted'}`}>
                  {a.description}
                </div>
                <div className="mt-1 flex items-center gap-2 font-mono text-[11px] text-muted">
                  <span>{relativeDate(a.detected_at)}</span>
                  {resolved && <Badge tone="success" size="sm">Resolved</Badge>}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {pg.pageCount > 1 && <PagedFooter className="mt-3" label="Anomalies pagination" {...pg} onPage={pg.setPage} />}
    </Box>
  );
}
